import { Location } from '@angular/common';
import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import {
    AppState,
    AuthClientConfig,
    AuthConfig,
    AuthService,
    User
} from '@auth0/auth0-angular';
import { Observable, firstValueFrom, race } from 'rxjs';
import { delay, map, take } from 'rxjs/operators';
import { QueryStringUtility } from '../core-utilities/QueryString.utility';
import { ConfigurationService } from '../system-configuration';
import { Logger } from '../system-services';
import { AuthenticatedUser, AuthenticationProxy } from './AuthenticationProxy';

export const ADDITIONAL_ALLOWED_HOSTS = new InjectionToken<string[]>(
    'additional.allowed.hosts',
    {
        providedIn: 'root',
        factory: () => []
    }
);

export const CONNECTION_STORAGE_KEY = 'pi_auth_connection';

function safeDecodeURIComponent(value: string): string {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
}

@Injectable({
    providedIn: 'root'
})
class Auth0SdkConfigBootstrapper {
    constructor(
        private config: ConfigurationService,
        private authSdkConfig: AuthClientConfig,
        private queryStringUtil: QueryStringUtility,
        @Optional()
        @Inject(ADDITIONAL_ALLOWED_HOSTS)
        private additionalHosts?: string[]
    ) {
        const allHosts = [
            ...this.config.getAllowedHostList(),
            ...(this.additionalHosts || [])
        ];

        const allowedList = [
            {
                uriMatcher: (uri: string) =>
                    allHosts.findIndex(
                        (value) =>
                            uri.toLowerCase().indexOf(value.toLowerCase()) > -1
                    ) > -1
            }
        ];

        const auth0Settings = this.config.getAuth0Settings();
        const connectionFromUrl =
            this.queryStringUtil.fetchQueryParameterValue('connection');

        // Persist connection across Auth0 redirects (error redirects drop the connection param).
        // The stored value is intentionally preserved on Auth0 error so that goToLogin() can
        // read it to build a clean appState.target (/?connection=...) for post-login redirection.
        // goToLogin() clears it after use on error redirects; logout() clears it on sign-out.
        if (connectionFromUrl) {
            sessionStorage.setItem(CONNECTION_STORAGE_KEY, connectionFromUrl);
        }

        // On Auth0 error, omit connection from the SDK config — the stored value may be
        // invalid and should not be used for token operations or silent auth.
        const hasAuthError =
            !!this.queryStringUtil.fetchQueryParameterValue('error');
        const connectionName = hasAuthError
            ? ''
            : connectionFromUrl ||
              sessionStorage.getItem(CONNECTION_STORAGE_KEY) ||
              '';

        const authConfig: AuthConfig = {
            domain: auth0Settings.domain,
            clientId: auth0Settings.clientID,
            useRefreshTokens: true,
            useRefreshTokensFallback: true,
            httpInterceptor: {
                allowedList: allowedList
            },
            authorizationParams: {
                audience: auth0Settings.apiUrl,
                redirect_uri: window.location.origin,
                ...(connectionName ? { connection: connectionName } : {})
            }
        };
        this.authSdkConfig.set(authConfig);
    }
}

@Injectable({
    providedIn: 'root'
})
export class Auth0SdkAuthenticationService implements AuthenticationProxy {
    private PROVIDER_KEY = 'https://www.predictiveindex.com/provider';
    private errors: Error[] = [];

    // you can't use "private router: Router" in here,
    // it hasn't been initialized by the time this service is used
    constructor(
        private _auth0SdkBootstrapper: Auth0SdkConfigBootstrapper, // This sets the SDK config. It should be the first dependency.
        private auth: AuthService,
        private config: ConfigurationService,
        private queryStringUtil: QueryStringUtility,
        private location: Location,
        private logger: Logger
    ) {
        this.auth.error$.subscribe((error) => {
            this.errors.push(error);
        });
    }

    clearErrors(): void {
        this.errors = [];
    }

    getErrors(): { hasErrors: boolean; errors: Error[] } {
        return { hasErrors: this.errors.length > 0, errors: this.errors };
    }

    getErrorStream(): Observable<Error> {
        return this.auth.error$;
    }

    getEmail(): Observable<string> {
        return this.auth.user$.pipe(map((user: User) => user.email));
    }

    logout(): void {
        sessionStorage.removeItem(CONNECTION_STORAGE_KEY);
        this.auth
            .logout({
                logoutParams: {
                    returnTo: window.location.origin
                }
            })
            .subscribe();
    }

    getToken(): Observable<string> {
        return this.auth.getAccessTokenSilently();
    }

    isAuthenticated(): Observable<boolean> {
        return this.auth.isAuthenticated$;
    }

    getUser(): Observable<AuthenticatedUser> {
        return this.auth.user$.pipe(
            map((user): AuthenticatedUser => {
                let provider = '';
                let email = '';
                try {
                    if (user !== null) {
                        provider = user[this.PROVIDER_KEY].split('|')[0];
                        email = user.email;
                    }
                } catch {
                    console.error('Check configured claims');
                }

                return new AuthenticatedUser(email, provider);
            }),
            take(1)
        );
    }

    async goToLogin(): Promise<any> {
        // Read connection from URL, falling back to sessionStorage for Auth0 error redirects
        // (error redirects from Auth0 drop the connection query param from the URL)
        const connectionName =
            this.queryStringUtil.fetchQueryParameterValue('connection') ||
            sessionStorage.getItem(CONNECTION_STORAGE_KEY) ||
            '';
        const redirectRequired = await this.redirectToResetPasswordPage();

        if (redirectRequired) {
            return;
        }

        // When Auth0 returns an error (e.g. invalid connection), it redirects back with
        // ?error=...&state=... — build a clean post-login target from the stored connection,
        // then clear the stored value so it is not retried on the next login attempt.
        const hasAuthError =
            !!this.queryStringUtil.fetchQueryParameterValue('error');
        if (hasAuthError) {
            sessionStorage.removeItem(CONNECTION_STORAGE_KEY);
        }
        const target = hasAuthError
            ? connectionName
                ? `/?connection=${encodeURIComponent(safeDecodeURIComponent(connectionName))}`
                : window.location.pathname
            : window.location.pathname + window.location.search;

        const loginOptions = {
            // correctly redirect to request path for bookmarks or deep links
            appState: { target },
            // Do not forward connection when Auth0 already returned an error — retrying
            // with the same connection would cause an immediate redirect loop.
            ...(connectionName && !hasAuthError
                ? { authorizationParams: { connection: connectionName } }
                : {})
        };
        this.auth.loginWithRedirect(loginOptions);
    }

    async goToApplication(): Promise<any> {
        const redirectRequired = await this.redirectToResetPasswordPage();

        if (redirectRequired) {
            return;
        }

        window.location.assign(window.location.origin + window.location.search);
    }

    async redirectToTargetPath(): Promise<boolean> {
        const appState = await this.getAppState();

        if (appState) {
            const targetPath =
                this.queryStringUtil.fetchQueryParameterFromString(
                    appState.target,
                    'path'
                );

            if (targetPath !== '') {
                window.location.assign(
                    window.location.origin + decodeURIComponent(targetPath)
                );

                return true;
            }
        }

        return false;
    }

    async fetchDeepLink(): Promise<string> {
        const result = await this.getAppState();
        return result ? result.target : '';
    }

    private async getAppState(): Promise<AppState> {
        const emptyObservable = new Observable<AppState>((observer) => {
            delay(20);
            observer.next(null);
            observer.complete();
        });

        // auth.appState$ only has a value when it's been set prior to login.
        // This typically only happens when the user is being deeplinked after
        // a password reset or something similar.
        // Unfortunately, auth.appState$ does not complete in that case.
        // To avoid hanging forever we return a null value after a small delay
        // to make sure that when auth.appState$ has data it wins the race
        // since the empty observable would otherwise return immediately
        // and might win when it shouldn't.
        return firstValueFrom(race(this.auth.appState$, emptyObservable));
    }

    private getDeepLinkPath() {
        return this.queryStringUtil.fetchQueryParameterValue('path');
    }

    private async redirectToResetPasswordPage(): Promise<boolean> {
        const userIdForPWChangeCheck =
            this.queryStringUtil.fetchQueryParameterValue('user_email');
        const tokenForPWChangeCheck =
            this.queryStringUtil.fetchHashParameterValue('user_token');

        if (userIdForPWChangeCheck && tokenForPWChangeCheck) {
            return await this.passwordChangeAction(
                userIdForPWChangeCheck,
                tokenForPWChangeCheck
            );
        }

        return false;
    }

    private async passwordChangeAction(
        userEmail: string,
        token: string
    ): Promise<boolean> {
        const path = this.getDeepLinkPath();

        // create query parameter if there is a path
        const pathQueryString = path ? `?path=${path}` : '';

        // construct redirectUrl to navigate to after user changes password, if they need to change password.
        // This will be passed to the BE and handled by Auth0.
        const redirectUrl = `${window.location.origin}/${pathQueryString}`;
        // Check if user needs to change password and either redirect to the change password URL returned from the BE
        // or continue with login and redirect to target path
        return await this.checkIfUserChangedInitialPasswordAndRedirect(
            userEmail,
            token,
            redirectUrl
        );
    }

    private async checkIfUserChangedInitialPasswordAndRedirect(
        userEmail: string,
        token: string,
        redirectUrl: string
    ): Promise<boolean> {
        const pathWithParams = `/api/v4/user/userActivity/?emailAddress=${userEmail}&path=${redirectUrl}&token=${token}`;

        try {
            const response: any = await fetch(
                this.config.getUrlWithHost(pathWithParams),
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            ).then((res) => res.json());

            // If this in our response JSON then we redirect to change password URL
            if (response.passwordResetUrl) {
                window.location.assign(response.passwordResetUrl);
                return true;
            }

            // User has changed password so continue with standard login
            this.stripPasswordResetQueryParams();
            return false;
        } catch (e) {
            // Unable to determine if user changed password so just continue with standard login
            this.logger.logError(
                'There was an issue retrieving the User Activity Document',
                e
            );
            this.stripPasswordResetQueryParams();
            return false;
        }
    }

    private stripPasswordResetQueryParams(): void {
        // Strip the email and token from the URL
        this.location.replaceState('', `path=${this.getDeepLinkPath()}`);
    }
}
